
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, CircularProgress, InputAdornment, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";

import PagePublic2Col from "../../components/Page/PagePublic2Col";
import EmailConfCard from "../Register/EmailConfCard";
import LanguageSelector from "../../language";
import ButtonsTextGoTo from "../../components/Button/ButtonTextGo";
import PasswordInput from "../../components/Forms/PasswordInput";
import { API_URL, IMAGE_RUN } from "../../constants";
import { useTranslation } from "../../language";


const passwordRegExp =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()[\]{};:'",.<>?\\|`~\-_=+]).{8,}$/;

const buildValidation = (t) => Yup.object({
  password: Yup.string()
    .matches(passwordRegExp, t("val_pw_strong"))
    .required(t("val_pw_req")),
  password_confirm: Yup.string()
    .oneOf([Yup.ref("password"), null], t("val_pw2_match"))
    .required(t("val_pw2_req")),
});


export default function ResetPasswordForm() {

  const { t } = useTranslation();
  const theme = useTheme();

  const { token } = useParams();

  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  return (
    <PagePublic2Col
      leftsideimage={IMAGE_RUN}
      brandtext="Preventive Health Intelligence"
      headline=""
      formcomp={
        <EmailConfCard
          topic=""
          content={<>

            <Box sx={{ mb: 4 }}><LanguageSelector /></Box>

            <Typography variant="h4" gutterBottom>
              {t("reset_4")}
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              {t("reset_5")}
            </Typography>

            <Formik
              initialValues={{ password: "", password_confirm: "" }}
              validationSchema={buildValidation(t)}
              onSubmit={async (values, { setSubmitting, setStatus }) => {
                try {
                  await axios.post(`${API_URL}/api/password_reset/confirm/`, {
                    token,
                    password: values.password,
                  });
                  setStatus({ success: true });
                } catch (error) {
                  const data = error?.response?.data;
                  const message =
                    data?.detail ||
                    (Array.isArray(data?.password) ? data.password.join(" ") : null) ||
                    (data?.status === "expired" ? t("reset_9") : null) ||
                    (data?.status === "notfound" ? t("reset_10") : null) ||
                    t("reset_11");
                  setStatus({ error: message });
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ handleSubmit, handleChange, values, isSubmitting, status }) => (
                <Box component="form" onSubmit={handleSubmit} noValidate>

                  <PasswordInput
                    label={t("reset_4")}
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={values.password}
                    onChange={handleChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword((p) => !p)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <PasswordInput
                    label={t("reset_7")}
                    name="password_confirm"
                    type={showPassword2 ? "text" : "password"}
                    value={values.password_confirm}
                    onChange={handleChange}
                    showStrength={false}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword2((p) => !p)} edge="end">
                            {showPassword2 ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {status?.success ? (
                    <>
                      <Typography variant="body2" align="center" color="success.main" sx={{ mt: 2, mb: 2 }}>
                        {t("reset_12")}
                      </Typography>
                      <ButtonsTextGoTo to="/Login" name={t("onb_email9")} />
                    </>
                  ) : (
                    <>
                      {status?.error && (
                        <Typography variant="body2" align="center" color="error" sx={{ mt: 2 }}>
                          {status.error}
                        </Typography>
                      )}
                      <Button
                        variant="text"
                        type="submit"
                        disabled={isSubmitting || !token}
                        sx={{
                          minWidth: { xs: 80, sm: 90, md: 100 },
                          p: '10px',
                          color: theme.palette.mode === 'light' ? 'common.black' : 'grey.C100',
                          fontWeight: 600,
                          transition: 'transform 0.2s ease',
                          '&:hover': {
                            p: '10px 12px',
                            color: 'common.white',
                            bgcolor: 'greydark.C900',
                            borderRadius: 5,
                            transform: 'scale(1.08)',
                          },
                        }}
                      >
                        {isSubmitting ? <CircularProgress size={18} color="inherit" /> : t("reset_6")}
                      </Button>
                    </>
                  )}

                </Box>
              )}
            </Formik>


          </>}
        />
      }
    />
  );
}
